import React, { useState } from 'react';
import {
  BooleanInput, Button, Datagrid, DateField, DateTimeInput, DeleteButton, Filter, FormWithRedirect,
  FunctionField, List, NumberInput, ResourceContextProvider, SaveButton, SelectInput, TextInput, TopToolbar,
  useCreate, useListContext, useNotify, useRefresh, useUpdate, required
} from 'react-admin';
import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import { Redirect } from 'react-router-dom';
import { useTranslate } from '../common/language';
import { choicesDonationsUsersPaymentMethod, modules, targetsEmails } from '../common/constants';
import { subscriptionModule } from '../common/utils';
import { SendEmailIconStatus } from './Emails';

const canEditPayments = () => ['SIFAC', 'OWNER', 'ADMIN', 'ADMIN_PAYMENTS']
  .includes(localStorage.getItem('permissions_groups'));

const canAccessPayments = () => localStorage.getItem('permissions_groups') === 'SIFAC' || (
  ['OWNER', 'ADMIN', 'ADMIN_PAYMENTS', 'ADMIN_READER'].includes(localStorage.getItem('permissions_groups'))
  && subscriptionModule(modules.FEDERATION)
);

const money = value => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('ca-ES', { style: 'currency', currency: 'EUR' })
    .format(Math.abs(amount) < 0.01 ? 0 : amount);
};
const amountWithTwoDecimals = value => Number(value || 0).toFixed(2);
const validateTwoDecimals = message => value => (
  value !== undefined && value !== null && value !== '' && !/^\d+(\.\d{1,2})?$/.test(String(value))
    ? message
    : undefined
);
const settlementPaymentMethods = translate => [
  ...choicesDonationsUsersPaymentMethod(translate),
  { id: 3, name: translate('superadmin_payments.bank_receipt') },
];
const settlementPaymentMethodLabel = (translate, paymentMethod) => paymentMethod === 3
  ? translate('superadmin_payments.bank_receipt')
  : translate(`donations_users.payment_method_${paymentMethod}`);

const PaymentDialog = ({ projectId, pendingAmount, settlementType, record, onClose }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [create, { loading: creating }] = useCreate('associations_payments');
  const [update, { loading: updating }] = useUpdate();
  const loading = creating || updating;

  const save = values => {
    const options = {
      onSuccess: () => {
        notify(
          translate(`superadmin_payments.${record ? 'updated' : 'registered'}_movement_${settlementType}`),
          'success'
        );
        onClose();
        refresh();
      },
      onFailure: error => notify(error.message, 'error'),
    };
    if (record) {
      update({
        type: 'update',
        resource: 'associations_payments',
        payload: { id: record.id, data: values, previousData: record },
      }, options);
    } else {
      create({ payload: { data: values } }, options);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{translate(record
        ? `superadmin_payments.edit_movement_${settlementType}`
        : `superadmin_payments.add_movement_${settlementType}`)}</DialogTitle>
      <FormWithRedirect
        save={save}
        initialValues={record ? { ...record, amount: amountWithTwoDecimals(record.amount) } : {
          projects_donations_id: projectId,
          date_payment: new Date().toISOString(),
          amount: amountWithTwoDecimals(pendingAmount),
          payment_system: 0,
          settlement_type: settlementType,
        }}
        render={({ handleSubmitWithRedirect, saving }) => (
          <>
            <DialogContent>
              <DateTimeInput fullWidth source="date_payment" label={translate(`superadmin_payments.date_movement_${settlementType}`)} validate={[required()]} />
              <NumberInput
                fullWidth
                source="amount"
                label={translate('superadmin_payments.amount')}
                step={0.01}
                validate={[required(), validateTwoDecimals(translate('superadmin_payments.amount_two_decimals'))]}
              />
              <SelectInput
                fullWidth
                source="payment_system"
                label={translate(`superadmin_payments.payment_method_movement_${settlementType}`)}
                choices={settlementPaymentMethods(translate)}
                validate={[required()]}
              />
              <TextInput
                fullWidth
                multiline
                source="comment"
                label={translate('superadmin_payments.comment')}
              />
            </DialogContent>
            <DialogActions>
              <Button label="ra.action.cancel" onClick={onClose} />
              <SaveButton handleSubmitWithRedirect={handleSubmitWithRedirect} saving={saving} disabled={loading} />
            </DialogActions>
          </>
        )}
      />
    </Dialog>
  );
};

const AddPaymentButton = ({ projectId, pendingAmount, settlementType }) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  if (!canEditPayments()) return null;
  return (
    <>
      <Button label={translate(`superadmin_payments.add_movement_${settlementType}`)} onClick={() => setOpen(true)}><AddIcon /></Button>
      {open ? <PaymentDialog projectId={projectId} pendingAmount={pendingAmount} settlementType={settlementType} onClose={() => setOpen(false)} /> : null}
    </>
  );
};

const EmptyPayments = ({ projectId, pendingAmount, settlementType }) => {
  const translate = useTranslate();
  return (
    <Box py={5} textAlign="center">
      <Typography variant="h6" className="grey8">
        {translate(`superadmin_payments.empty_movements_${settlementType}`)}
      </Typography>
      <Box mt={2}>
        <AddPaymentButton projectId={projectId} pendingAmount={pendingAmount} settlementType={settlementType} />
      </Box>
    </Box>
  );
};

const EditPaymentButton = ({ record }) => {
  const [open, setOpen] = useState(false);
  if (!canEditPayments()) return null;
  return (
    <span onClick={event => event.stopPropagation()}>
      <Button label="" onClick={() => setOpen(true)}><EditIcon /></Button>
      {open ? <PaymentDialog projectId={record.projects_donations_id} settlementType={record.settlement_type} record={record} onClose={() => setOpen(false)} /> : null}
    </span>
  );
};

const PaymentsActions = ({ projectId, pendingAmount, settlementType }) => (
  <TopToolbar>
    <AddPaymentButton projectId={projectId} pendingAmount={pendingAmount} settlementType={settlementType} />
  </TopToolbar>
);

const ProjectPayments = ({ record }) => {
  const translate = useTranslate();
  if (!record) return null;
  const settlementType = record.settlement_type || 0;
  const pendingAmount = settlementType === 0 ? record.pending_transfer : record.pending_collection;
  return (
    <Box p={2}>
      <ResourceContextProvider value="associations_payments">
        <List
          resource="associations_payments"
          basePath="/associations_payments"
          filter={{ projects_donations_id: record.id, settlement_type: settlementType }}
          actions={canEditPayments()
            ? <PaymentsActions projectId={record.id} pendingAmount={pendingAmount} settlementType={settlementType} />
            : false}
          empty={<EmptyPayments projectId={record.id} pendingAmount={pendingAmount} settlementType={settlementType} />}
          bulkActionButtons={false}
          sort={{ field: 'datePayment', order: 'DESC' }}
          perPage={25}
          title=""
        >
          <Datagrid className="liquidations-detail-grid">
            <DateField source="date_payment" label={translate(`superadmin_payments.date_movement_${settlementType}`)} showTime sortable={false} />
            <FunctionField textAlign="right" label={translate('superadmin_payments.amount')} render={payment => money(payment.amount)} />
            <FunctionField
              label={translate(`superadmin_payments.payment_method_movement_${settlementType}`)}
              render={payment => settlementPaymentMethodLabel(translate, payment.payment_system)}
            />
            <FunctionField label={translate('superadmin_payments.comment_column')} render={payment => payment.comment} />
            {canEditPayments() ? <EditPaymentButton /> : null}
            {canEditPayments() ? (
              <DeleteButton
                undoable={false}
                redirect={false}
                label=""
                confirmTitle={translate('superadmin_payments.delete_payment')}
                confirmContent={translate('superadmin_payments.delete_payment_confirm')}
              />
            ) : null}
          </Datagrid>
        </List>
      </ResourceContextProvider>
    </Box>
  );
};

const PaymentsFilter = props => {
  const translate = useTranslate();
  return (
    <Filter {...props}>
      <TextInput source="q" label={translate('common.search')} alwaysOn resettable />
      <SelectInput
        source="mode"
        label={translate('superadmin_payments.mode')}
        choices={[
          { id: 'donations', name: translate('superadmin_payments.donations') },
          { id: 'tickets', name: translate('superadmin_payments.tickets') },
        ]}
        allowEmpty={false}
        alwaysOn
      />
      <SelectInput
        source="settlement_type"
        label={translate('superadmin_payments.settlement_type')}
        choices={[
          { id: 0, name: translate('superadmin_payments.payments') },
          { id: 1, name: translate('superadmin_payments.collections') },
        ]}
        allowEmpty={false}
        alwaysOn
      />
      <BooleanInput source="only_pending" label={translate('superadmin_payments.only_pending')} alwaysOn />
    </Filter>
  );
};

const PaymentsDatagrid = () => {
  const translate = useTranslate();
  const { filterValues } = useListContext();
  const settlementType = Number(filterValues.settlement_type || 0);
  if (filterValues.mode === 'tickets') {
    return <Typography>{translate('superadmin_payments.tickets_pending')}</Typography>;
  }
  return (
    <Datagrid className="liquidations-main-grid" expand={<ProjectPayments />}>
      <FunctionField label={translate('superadmin_payments.association')} render={record => record.associations_name} />
      <FunctionField label={translate('superadmin_payments.project')} render={record => record.project_name} />
      <FunctionField label={translate('superadmin_payments.project_status')} render={record => translate(record.project_status === 1 ? 'superadmin_payments.finished' : 'superadmin_payments.in_progress')} />
      <DateField source="date_finish" label={translate('superadmin_payments.date_finish')} />
      <FunctionField textAlign="right" label={translate('superadmin_payments.total_collected')} render={record => money(record.total_collected)} />
      <FunctionField textAlign="right" label={translate('superadmin_payments.management_expenses')} render={record => `${money(record.management_expenses)} (${record.management_percent}%)`} />
      <FunctionField textAlign="right" label={translate('superadmin_payments.management_tax')} render={record => `${money(record.management_tax)} (${record.management_tax_percent}%)`} />
      {settlementType === 0 ? <FunctionField textAlign="right" label={translate('superadmin_payments.total_to_transfer')} render={record => money(record.total_to_transfer)} /> : null}
      {settlementType === 0 ? <FunctionField textAlign="right" label={translate('superadmin_payments.total_transferred')} render={record => money(record.total_transferred)} /> : null}
      {settlementType === 0 ? <FunctionField textAlign="right" label={translate('superadmin_payments.pending_transfer')} render={record => money(record.pending_transfer)} /> : null}
      {settlementType === 1 ? <FunctionField textAlign="right" label={translate('superadmin_payments.total_to_collect')} render={record => money(record.total_to_collect)} /> : null}
      {settlementType === 1 ? <FunctionField textAlign="right" label={translate('superadmin_payments.total_already_collected')} render={record => money(record.total_already_collected)} /> : null}
      {settlementType === 1 ? <FunctionField textAlign="right" label={translate('superadmin_payments.pending_collection')} render={record => money(record.pending_collection)} /> : null}
      {canEditPayments() ? (
        <FunctionField
          label={translate('superadmin_payments.send_statement')}
          render={record => <SendEmailIconStatus record={record} target={targetsEmails.DONATIONS_LIQUIDATION_STATEMENT} />}
        />
      ) : null}
    </Datagrid>
  );
};

const DonationsPaymentsList = () => {
  return (
    <ResourceContextProvider value="associations_payments/projects">
      <List
        resource="associations_payments/projects"
        basePath="/associations_payments/projects"
        bulkActionButtons={false}
        actions={false}
        filters={<PaymentsFilter />}
        filterDefaultValues={{ mode: 'donations', settlement_type: 0, only_pending: true }}
        perPage={25}
        sort={{ field: 'id', order: 'DESC' }}
        title=""
      >
        <PaymentsDatagrid />
      </List>
    </ResourceContextProvider>
  );
};

export const SuperadminPayments = () => {
  const translate = useTranslate();
  if (!canAccessPayments()) return <Redirect to="/not-found" />;
  return (
    <Box p={3}>
      <Typography variant="h5" className="grey8" align="center" gutterBottom>{translate('superadmin_payments.title')}</Typography>
      <Box mt={3}>
        <DonationsPaymentsList />
      </Box>
    </Box>
  );
};
